import {
    useCallback,
    useMemo,
} from 'react'
import {
    useParams,
} from 'react-router-dom'

import {
    RequestKeys,
} from 'app/hooks/useRequest'
import useLabel from 'app/hooks/useLabel'
import {
    useGetRequestSpreadSheetAdapter,
} from 'app/shared-components/SkyNetSpreadSheet'
import useSimpleApiRequest from 'app/hooks/useSimpleApiRequest'
import StatusHandler from 'app/shared-components/StatusHandler'
import {
    AsyncMutationOptions,
} from 'app/types/request.types'
import useFilter from 'app/hooks/useFilter'
import {
    useSkyNetPagination, SkyNetPaginationContext,
} from 'app/shared-components/SkyNetPagination'
import requests from 'app/requests/APricingRateCard/APricingRateCard.requests'
import {
    APricingStandardBaseLeaseFilterRequestResponse,
} from 'app/requests/APricingRateCard/APricingRateCard.types'
import BaseLeaseSpreadsheet from 'app/Apps/Rates/components/BaseLeaseSpreadsheet'
import {
    BaseLeaseEditable,
} from 'app/Apps/Rates/RateCards/BaseLease/BaseLease.types'
import {
    prepareBaseLeaseToBulkEdit,
} from 'app/Apps/Rates/RateCards/BaseLease/BaseLease.utils'
import defaultRateCardsSort from 'app/Apps/Rates/Rates.utils'

export default function APricingStandardBaseLeaseBulkEdit({
    onClose,
}: Readonly<{
    onClose: () => void,
}>) {
    const l = useLabel()
    const {
        key,
    } = useParams()
    const rateCardId = Number(key)
    const filterOptions = useFilter<APricingStandardBaseLeaseFilterRequestResponse[]>()
    const paginationOptions = useSkyNetPagination(100)

    const config = useCallback(
        (params: Record<string, unknown>) => {
            return requests.standardBaseLeaseFilterRequest({
                rateCardId,
                params,
            })
        },
        [rateCardId],
    )

    const {
        isError, loaded, error, refetch,
    } = useGetRequestSpreadSheetAdapter<APricingStandardBaseLeaseFilterRequestResponse>(
        {
            config,
            requestKey:
            RequestKeys.getAPricingRateCardBaseLeaseFilterRequest,
            enabled: true,
            filterOptions,
            paginationOptions,
            defaultSort: defaultRateCardsSort,
        },
    )

    const {
        mutateAsync: bulkEdit,
    } = useSimpleApiRequest<{
        items: BaseLeaseEditable[]
    }>({
        name: 'APricingStandardBaseLeaseBulkUpdate',
        title: l('STANDARD_BASE_LEASE_RATE_CARDS_UPDATED'),
        requestParams: requests.updateStandardBaseLeaseRateCardBulk({
            rateCardId,
        }),
        onSuccess: refetch,
    })

    const onSave = useCallback(
        async (
            items: APricingStandardBaseLeaseFilterRequestResponse[],
            options?: AsyncMutationOptions,
        ) => {
            await bulkEdit(
                {
                    items: prepareBaseLeaseToBulkEdit(items),
                },
                {
                    ...options,
                    onSuccess: options?.onSuccess || onClose,
                },
            )
        },
        [
            bulkEdit,
            onClose,
        ],
    )

    const filterConfigRequest = useMemo(() => {
        return ({
            fieldName,
        }: { fieldName: string }) => {
            return requests.standardBaseLeaseFilterRequestCount({
                rateCardId,
                fieldName,
            })
        }
    }, [rateCardId])

    return (
        <StatusHandler
            isError={isError}
            isFetching={!loaded}
            error={error}
            isSuccess={loaded}
        >
            <SkyNetPaginationContext.Provider value={paginationOptions}>
                <BaseLeaseSpreadsheet
                    filterOptions={filterOptions}
                    onCancel={onClose}
                    onSave={onSave}
                    filterConfigRequest={filterConfigRequest}
                />
            </SkyNetPaginationContext.Provider>
        </StatusHandler>
    )
}
